import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ChevronDown, MapPin, Landmark, Trophy } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { AIContent } from "../components/AIContent";
import { T } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CountriesPage() {
  const [countries, setCountries] = useState([]);
  const [open, setOpen] = useState(null);
  const [continent, setContinent] = useState("All");
  const [search, setSearch] = useState("");

  useEffect(() => {
    axios.get(`${API}/countries`).then((r) => setCountries(r.data)).catch(() => {});
  }, []);

  const continents = ["All", ...new Set(countries.map((c) => c.continent))];
  const shown = countries.filter((c) =>
    (continent === "All" || c.continent === continent) &&
    (!search.trim() || c.name.toLowerCase().includes(search.trim().toLowerCase()))
  );

  return (
    <div>
      <PageHero topic="world flags stadium crowd" title="Countries & Places" subtitle="Every country, state or province, and city can have a living sports page — connecting local participation to the global game." listenTargetId="page-content" />
      <div id="page-content" className="mx-auto max-w-7xl space-y-10 px-4 py-12 sm:px-6">
        <AIContent topic="How geography shapes world sport — national identities, regional pathways and local participation" context="Editorial on why organizing sport by place matters: national systems, state associations, county clubs and village pitches." />
        <div className="flex flex-wrap items-center gap-3">
          <input
            data-testid="country-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search 250 countries…"
            className="w-full max-w-xs rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm outline-none focus:border-[#0055FF]"
          />
          <div className="flex flex-wrap gap-2" data-testid="continent-filters">
            {continents.map((c) => (
              <button key={c} data-testid={`continent-filter-${c.toLowerCase().replace(/\s/g, "-")}`} onClick={() => setContinent(c)} className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${continent === c ? "bg-[#0055FF] text-white" : "border border-slate-200 bg-white text-slate-600 hover:border-[#0055FF] hover:text-[#0055FF]"}`}>
                {c === "All" ? <T>All</T> : <T>{c}</T>}
              </button>
            ))}
          </div>
          <p className="text-sm font-semibold text-slate-400" data-testid="country-count">{shown.length} <T>countries</T></p>
        </div>
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2" data-testid="countries-grid">
          {shown.map((c) => (
            <div key={c.id} data-testid={`country-card-${c.id}`} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <button data-testid={`country-toggle-${c.id}`} onClick={() => setOpen(open === c.id ? null : c.id)} className="flex w-full items-center justify-between text-left">
                <div className="flex items-center gap-3">
                  <span className="text-4xl">{c.flag}</span>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">{c.name} {c.curated && <span className="ml-1 rounded-full bg-amber-100 px-2 py-0.5 align-middle text-[10px] font-bold uppercase text-amber-700"><T>Featured</T></span>}</h3>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400"><T>{c.continent}</T>{c.capital ? ` · ${c.capital}` : ""}</p>
                  </div>
                </div>
                <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform ${open === c.id ? "rotate-180" : ""}`} />
              </button>
              <Link to={`/countries/${c.id}`} data-testid={`country-page-link-${c.id}`} className="mt-3 inline-flex items-center gap-1 rounded-full bg-blue-50 px-3.5 py-1.5 text-xs font-bold text-[#0055FF] hover:bg-blue-100"><T>Full country page</T> →</Link>
              {(c.top_sports || []).length > 0 && (
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {c.top_sports.map((s) => <span key={s} className="rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-bold text-[#0055FF]">{s}</span>)}
                </div>
              )}
              {(c.olympic_committee || c.federations) && (
                <div className="mt-4 grid grid-cols-1 gap-2 text-xs text-slate-600 sm:grid-cols-2">
                  {c.olympic_committee && <p className="flex items-center gap-1.5"><Landmark className="h-3.5 w-3.5 text-slate-400" /> <span className="font-bold"><T>Olympic committee</T>:</span> {c.olympic_committee}</p>}
                  {c.federations && <p className="flex items-center gap-1.5"><Trophy className="h-3.5 w-3.5 text-slate-400" /> <span className="font-bold"><T>Federations</T>:</span> {c.federations.join(", ")}</p>}
                </div>
              )}
              {open === c.id && (
                <div className="mt-5 space-y-3 border-t border-slate-100 pt-4" data-testid={`country-detail-${c.id}`}>
                  <AIContent topic={`Sporting culture of ${c.name}`} context={`Write a short sporting-culture overview of ${c.name} (${c.continent}). Cover popular sports, participation traditions and how communities play. Speak in general, well-known terms — do not invent specific clubs, statistics, results or federation names.`} />
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-400"><T>States, provinces & cities</T></p>
                  {c.states.length === 0 && <p className="text-sm text-slate-500"><T>No administrative divisions listed for this country.</T></p>}
                  {c.states.map((st) => (
                    <div key={st.name} className="rounded-xl bg-slate-50 p-3">
                      <p className="flex items-center gap-1.5 text-sm font-bold text-slate-800"><MapPin className="h-3.5 w-3.5 text-[#0055FF]" /> {st.name}</p>
                      {st.cities.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {st.cities.map((city) => <Link key={city} to={`/cities/${encodeURIComponent(city)}`} data-testid={`city-link-${city.toLowerCase().replace(/\s/g, "-")}`} className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-[#0055FF] border border-slate-200 hover:border-[#0055FF]">{city} →</Link>)}
                        </div>
                      )}
                    </div>
                  ))}
                  {c.halls_of_fame && <p className="text-xs text-slate-500"><span className="font-bold"><T>Halls of fame</T>:</span> {c.halls_of_fame.join(", ")}</p>}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
